// IPv4 subnet math for the subnet calculator. Pure integer arithmetic on the
// 32-bit address, so it matches ipcalc / sipcalc output exactly.

export type SubnetResult = {
  address: string;
  cidr: number;
  mask: string;
  wildcard: string;
  network: string;
  broadcast: string;
  firstHost: string;
  lastHost: string;
  totalAddresses: number;
  usableHosts: number;
  ipClass: string;
  scope: string;
  binaryMask: string;
};

const toInt = (ip: string): number => {
  const parts = ip.trim().split('.');
  if (parts.length !== 4 || parts.some((p) => !/^\d{1,3}$/.test(p) || Number(p) > 255))
    throw new Error('Enter an IPv4 address like 192.168.1.10');
  return parts.reduce((acc, p) => acc * 256 + Number(p), 0);
};
const toIp = (n: number) => [24, 16, 8, 0].map((s) => Math.floor(n / 2 ** s) % 256).join('.');

export function parseCidrInput(addressInput: string, prefixInput: string): [string, number] {
  const [addr, slash] = addressInput.split('/');
  const raw = (slash ?? prefixInput).trim();
  let prefix: number;
  if (raw.includes('.')) {
    // A dotted mask ("255.255.255.0") becomes its prefix length, if contiguous.
    const m = toInt(raw);
    const bits = m.toString(2).padStart(32, '0');
    if (!/^1*0*$/.test(bits)) throw new Error('That subnet mask is not contiguous.');
    prefix = bits.indexOf('0') === -1 ? 32 : bits.indexOf('0');
  } else {
    prefix = Number(raw);
  }
  if (!Number.isInteger(prefix) || prefix < 0 || prefix > 32)
    throw new Error('Prefix must be 0 to 32 (or a mask like 255.255.255.0).');
  return [addr.trim(), prefix];
}

export function subnet(address: string, prefix: number): SubnetResult {
  const ip = toInt(address);
  const size = 2 ** (32 - prefix);
  const network = Math.floor(ip / size) * size;
  const broadcast = network + size - 1;
  const maskInt = 2 ** 32 - size;
  const usable = prefix >= 31 ? size : Math.max(0, size - 2);
  const first = Math.floor(ip / 2 ** 24);
  const ipClass =
    first < 128 ? 'A' : first < 192 ? 'B' : first < 224 ? 'C' : first < 240 ? 'D (multicast)' : 'E';
  const inRange = (base: string, bits: number) => {
    const b = toInt(base);
    const s = 2 ** (32 - bits);
    return Math.floor(ip / s) * s === b;
  };
  const scope =
    inRange('10.0.0.0', 8) || inRange('172.16.0.0', 12) || inRange('192.168.0.0', 16)
      ? 'Private (RFC 1918)'
      : inRange('127.0.0.0', 8)
        ? 'Loopback'
        : inRange('169.254.0.0', 16)
          ? 'Link-local'
          : inRange('100.64.0.0', 10)
            ? 'Carrier-grade NAT (RFC 6598)'
            : 'Public';
  return {
    address: toIp(ip),
    cidr: prefix,
    mask: toIp(maskInt),
    wildcard: toIp(2 ** 32 - 1 - maskInt),
    network: toIp(network),
    broadcast: toIp(broadcast),
    // /31 point-to-point links and /32 hosts use every address (RFC 3021).
    firstHost: toIp(prefix >= 31 ? network : network + 1),
    lastHost: toIp(prefix >= 31 ? broadcast : broadcast - 1),
    totalAddresses: size,
    usableHosts: usable,
    ipClass,
    scope,
    binaryMask: maskInt
      .toString(2)
      .padStart(32, '0')
      .replace(/(.{8})(?!$)/g, '$1.'),
  };
}
